/**
 * What each effect spawns, as plain data: pure functions from an event to a list of particle
 * specifications, varied by a tiny seeded generator so tests can pin them. The system decides
 * how they are drawn; the rules decide nothing here (effects are cosmetic).
 */
export type PoolName = 'additive' | 'smoke' | 'debris';

export interface ParticleSpec {
  pool: PoolName;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Seconds. */
  life: number;
  /** World size in metres (a sprite's edge, a chunk's scale). */
  size: number;
  /** Vertical acceleration in m/s² (negative falls). */
  gravity: number;
  /** Velocity damping per second (0 = none). */
  drag: number;
  /** Bounce off the floor (debris). */
  bounce: boolean;
  r: number;
  g: number;
  b: number;
  /** 0: bright then fade (flashes, sparks); 1: swell and thin (smoke, steam, dust). */
  fade: 0 | 1;
}

/** A small deterministic generator (visual variety only). */
export class VfxRng {
  private state: number;

  constructor(seed = 0x9e3779b9) {
    this.state = seed >>> 0 || 1;
  }

  /** Uniform in [0, 1). */
  next(): number {
    // xorshift32
    let x = this.state;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.state = x >>> 0;
    return this.state / 0x100000000;
  }

  /** Uniform in [-1, 1). */
  signed(): number {
    return this.next() * 2 - 1;
  }
}

interface Vec {
  x: number;
  y: number;
  z: number;
}

const SPARK = { r: 1.0, g: 0.62, b: 0.22 };
const FLASH = { r: 1.0, g: 0.78, b: 0.5 };
const FIRE = { r: 1.0, g: 0.42, b: 0.1 };
const EMBER = { r: 1.0, g: 0.5, b: 0.14 };
const JET = { r: 0.55, g: 0.8, b: 1.0 };
const SMOKE = { r: 0.32, g: 0.32, b: 0.34 };
const STEAM = { r: 0.78, g: 0.82, b: 0.86 };
const DUST = { r: 0.72, g: 0.62, b: 0.46 };

function spec(
  pool: PoolName,
  at: Vec,
  vel: Vec,
  life: number,
  size: number,
  colour: { r: number; g: number; b: number },
  extra: Partial<Pick<ParticleSpec, 'gravity' | 'drag' | 'bounce' | 'fade'>> = {},
): ParticleSpec {
  return {
    pool,
    x: at.x,
    y: at.y,
    z: at.z,
    vx: vel.x,
    vy: vel.y,
    vz: vel.z,
    life,
    size,
    gravity: extra.gravity ?? 0,
    drag: extra.drag ?? 0,
    bounce: extra.bounce ?? false,
    r: colour.r,
    g: colour.g,
    b: colour.b,
    fade: extra.fade ?? 0,
  };
}

/** A random unit-ish direction around `axis` within a cone (fraction 0 = along the axis). */
function scatter(rng: VfxRng, axis: Vec, spread: number): Vec {
  const x = axis.x + rng.signed() * spread;
  const y = axis.y + rng.signed() * spread;
  const z = axis.z + rng.signed() * spread;
  const len = Math.hypot(x, y, z) || 1;
  return { x: x / len, y: y / len, z: z / len };
}

function scale(v: Vec, k: number): Vec {
  return { x: v.x * k, y: v.y * k, z: v.z * k };
}

/** A shot's flash at the muzzle plus a few sparks along the barrel. */
export function muzzleFlash(origin: Vec, dir: Vec, rng: VfxRng): ParticleSpec[] {
  const out: ParticleSpec[] = [];
  for (let i = 0; i < 2; i++) {
    out.push(
      spec('additive', origin, scale(dir, 2 + i * 2), 0.08 + i * 0.03, 0.55 + i * 0.25, FLASH),
    );
  }
  for (let i = 0; i < 6; i++) {
    const d = scatter(rng, dir, 0.35);
    out.push(
      spec(
        'additive',
        origin,
        scale(d, 9 + rng.next() * 6),
        0.18 + rng.next() * 0.12,
        0.14,
        SPARK,
        {
          gravity: -18,
          drag: 2,
        },
      ),
    );
  }
  return out;
}

/** Sparks and a puff where a shot or projectile lands. */
export function impact(at: Vec, normal: Vec | null, rng: VfxRng, count = 12): ParticleSpec[] {
  const n = normal ?? { x: 0, y: 1, z: 0 };
  const out: ParticleSpec[] = [];
  for (let i = 0; i < count; i++) {
    const d = scatter(rng, n, 0.9);
    out.push(
      spec('additive', at, scale(d, 5 + rng.next() * 6), 0.3 + rng.next() * 0.25, 0.12, SPARK, {
        gravity: -24,
        drag: 1.5,
      }),
    );
  }
  for (let i = 0; i < 4; i++) {
    const d = scatter(rng, n, 1.2);
    out.push(
      spec('smoke', at, scale(d, 1.5 + rng.next()), 0.9 + rng.next() * 0.4, 0.45, SMOKE, {
        drag: 2.5,
        fade: 1,
      }),
    );
  }
  for (let i = 0; i < 3; i++) {
    const d = scatter(rng, n, 1.0);
    out.push(
      spec('debris', at, scale(d, 4 + rng.next() * 4), 1.2 + rng.next() * 0.6, 0.12, SMOKE, {
        gravity: -24,
        drag: 0.6,
        bounce: true,
      }),
    );
  }
  return out;
}

/** Fire, embers, smoke and debris in proportion to the blast radius. */
export function explosion(at: Vec, radius: number, rng: VfxRng): ParticleSpec[] {
  const r = Math.max(1, radius);
  const out: ParticleSpec[] = [];
  const up = { x: 0, y: 1, z: 0 };
  for (let i = 0; i < 24; i++) {
    const d = scatter(rng, up, 1.6);
    out.push(
      spec(
        'additive',
        at,
        scale(d, r * (1 + rng.next() * 2)),
        0.35 + rng.next() * 0.25,
        r * 0.5,
        FIRE,
        {
          drag: 3,
        },
      ),
    );
  }
  for (let i = 0; i < 32; i++) {
    const d = scatter(rng, up, 1.8);
    out.push(
      spec(
        'additive',
        at,
        scale(d, r * (4 + rng.next() * 5)),
        0.5 + rng.next() * 0.6,
        0.14,
        EMBER,
        {
          gravity: -20,
          drag: 1.2,
        },
      ),
    );
  }
  for (let i = 0; i < 16; i++) {
    const d = scatter(rng, up, 1.4);
    out.push(
      spec(
        'smoke',
        at,
        scale(d, r * (0.8 + rng.next() * 1.2)),
        1.4 + rng.next() * 0.8,
        r * 0.8,
        SMOKE,
        {
          drag: 1.8,
          fade: 1,
        },
      ),
    );
  }
  for (let i = 0; i < 12; i++) {
    const d = scatter(rng, up, 1.2);
    out.push(
      spec('debris', at, scale(d, r * (3 + rng.next() * 5)), 1.5 + rng.next() * 0.8, 0.16, SMOKE, {
        gravity: -24,
        drag: 0.5,
        bounce: true,
      }),
    );
  }
  return out;
}

/** One puff behind a projectile. */
export function projectileTrail(at: Vec, velocity: Vec, rng: VfxRng): ParticleSpec[] {
  const back = scale(velocity, -0.05);
  return [
    spec(
      'smoke',
      at,
      { x: back.x + rng.signed() * 0.4, y: back.y + 0.3, z: back.z + rng.signed() * 0.4 },
      0.5 + rng.next() * 0.2,
      0.32,
      SMOKE,
      { drag: 2, fade: 1 },
    ),
  ];
}

/** The jets' exhaust while a mech boosts or flies: hot cores pushed down and back. */
export function jetExhaust(at: Vec, back: Vec, rng: VfxRng): ParticleSpec[] {
  const out: ParticleSpec[] = [];
  for (let i = 0; i < 2; i++) {
    const d = scatter(rng, { x: back.x * 0.6, y: -1, z: back.z * 0.6 }, 0.4);
    out.push(
      spec('additive', at, scale(d, 6 + rng.next() * 4), 0.22 + rng.next() * 0.1, 0.32, JET, {
        drag: 4,
      }),
    );
  }
  return out;
}

/** Venting: steam rising off the mech. */
export function ventSteam(at: Vec, rng: VfxRng): ParticleSpec[] {
  return [
    spec(
      'smoke',
      at,
      { x: rng.signed() * 0.6, y: 2.5 + rng.next(), z: rng.signed() * 0.6 },
      0.9 + rng.next() * 0.4,
      0.45,
      STEAM,
      { drag: 1.5, fade: 1 },
    ),
  ];
}

/** A broken shield bursts into shards of the mech's accent colour. */
export function shieldShards(
  at: Vec,
  accent: { r: number; g: number; b: number },
  rng: VfxRng,
): ParticleSpec[] {
  const out: ParticleSpec[] = [];
  for (let i = 0; i < 24; i++) {
    const d = scatter(rng, { x: 0, y: 0.2, z: 0 }, 1.5);
    out.push(
      spec('additive', at, scale(d, 5 + rng.next() * 5), 0.4 + rng.next() * 0.3, 0.2, accent, {
        gravity: -10,
        drag: 2,
      }),
    );
  }
  return out;
}

/** Steam drifting up out of a hazard's volume. */
export function hazardSteam(
  center: Vec,
  halfExtents: Vec,
  rng: VfxRng,
  colour: { r: number; g: number; b: number } = STEAM,
): ParticleSpec[] {
  const at = {
    x: center.x + rng.signed() * halfExtents.x,
    y: center.y - halfExtents.y + 0.2,
    z: center.z + rng.signed() * halfExtents.z,
  };
  return [
    spec(
      'smoke',
      at,
      { x: rng.signed() * 0.3, y: 1 + rng.next(), z: rng.signed() * 0.3 },
      1.2 + rng.next() * 0.8,
      0.7,
      colour,
      {
        drag: 0.8,
        fade: 1,
      },
    ),
  ];
}

/** Dust kicked up by a landing or a heavy step. */
export function dustKick(at: Vec, rng: VfxRng, count = 6): ParticleSpec[] {
  const out: ParticleSpec[] = [];
  for (let i = 0; i < count; i++) {
    const a = rng.next() * Math.PI * 2;
    out.push(
      spec(
        'smoke',
        at,
        {
          x: Math.cos(a) * (1.5 + rng.next()),
          y: 0.6 + rng.next() * 0.5,
          z: Math.sin(a) * (1.5 + rng.next()),
        },
        0.6 + rng.next() * 0.3,
        0.5,
        DUST,
        { drag: 3, fade: 1 },
      ),
    );
  }
  return out;
}

/** Steam pouring out of an overheated mech. */
export function overheatBurst(at: Vec, rng: VfxRng): ParticleSpec[] {
  const out: ParticleSpec[] = [];
  for (let i = 0; i < 10; i++) {
    const d = scatter(rng, { x: 0, y: 1, z: 0 }, 1.0);
    out.push(
      spec('smoke', at, scale(d, 3 + rng.next() * 2), 1.0 + rng.next() * 0.5, 0.55, STEAM, {
        drag: 1.5,
        fade: 1,
      }),
    );
  }
  return out;
}
